import { useEffect, useState, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { ThemeConfig, DEFAULT_THEME } from "@/types/theme";
import { logError } from "@/lib/logger";
import { getContrastingForeground, getMutedForeground } from "@/lib/color-utils";

const THEME_CACHE_KEY = 'theme-config-cache';
const THEME_CACHE_TTL = 1000 * 60 * 5; // 5 minutes

interface ThemeCache {
  theme: ThemeConfig;
  timestamp: number;
}

export const useThemeConfig = () => {
  const [theme, setTheme] = useState<ThemeConfig>(DEFAULT_THEME);
  const [isLoading, setIsLoading] = useState(false);
  const themeRef = useRef<ThemeConfig>(DEFAULT_THEME);

  // Load theme on mount and subscribe to changes
  useEffect(() => {
    const savedMode = localStorage.getItem('theme-mode');
    if (savedMode) {
      document.documentElement.classList.toggle('dark', savedMode === 'dark');
    }

    // Apply default theme immediately so pages can render without waiting on Supabase
    applyTheme(DEFAULT_THEME);
    loadTheme();

    // Subscribe to theme config changes for real-time updates
    const subscription = supabase
      .channel('theme-config-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'theme_config'
        },
        (payload) => {
          console.log('[useThemeConfig] Real-time theme update received:', payload);
          if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
            const newTheme = payload.new as ThemeConfig;
            setTheme(newTheme);
            themeRef.current = newTheme;
            applyTheme(newTheme);
            
            // Update cache
            localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({
              theme: newTheme,
              timestamp: Date.now()
            }));
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Handle dark mode changes separately
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isDark = document.documentElement.classList.contains('dark');
          localStorage.setItem('theme-mode', isDark ? 'dark' : 'light');
          applyTheme(themeRef.current);
        }
      });
    });
    
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return () => observer.disconnect();
  }, []);

  const loadTheme = async () => {
    try {
      // Check cache first
      const cachedData = localStorage.getItem(THEME_CACHE_KEY);
      if (cachedData) {
        const cache: ThemeCache = JSON.parse(cachedData);
        if (Date.now() - cache.timestamp < THEME_CACHE_TTL) {
          setTheme(cache.theme);
          themeRef.current = cache.theme;
          applyTheme(cache.theme);
          return;
        }
      }

      const { data, error } = await supabase
        .from('theme_config')
        .select('*')
        .single();

      if (error) throw error;

      // Map database fields to ThemeConfig
      const loadedTheme: ThemeConfig = data ? {
        id: data.id,
        primary_color_light: data.primary_color_light || DEFAULT_THEME.primary_color_light,
        primary_color_dark: data.primary_color_dark || DEFAULT_THEME.primary_color_dark,
        secondary_color_light: data.secondary_color_light || DEFAULT_THEME.secondary_color_light,
        secondary_color_dark: data.secondary_color_dark || DEFAULT_THEME.secondary_color_dark,
        accent_color_light: data.accent_color_light || DEFAULT_THEME.accent_color_light,
        accent_color_dark: data.accent_color_dark || DEFAULT_THEME.accent_color_dark,
        background_light: data.background_light || DEFAULT_THEME.background_light,
        background_dark: data.background_dark || DEFAULT_THEME.background_dark,
        created_at: data.created_at,
        updated_at: data.updated_at,
      } : DEFAULT_THEME;
      
      setTheme(loadedTheme);
      themeRef.current = loadedTheme;
      applyTheme(loadedTheme);
      
      // Cache the theme
      localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({
        theme: loadedTheme,
        timestamp: Date.now()
      }));
    } catch (error) {
      logError(error, 'useThemeConfig.loadTheme');
      // DEFAULT_THEME already applied on mount, nothing to fall back to
    }
  };

  const applyTheme = (themeConfig: ThemeConfig) => {
    const root = document.documentElement;
    const isDark = root.classList.contains('dark');
    
    // Select colors based on mode
    const primaryColor = isDark ? themeConfig.primary_color_dark : themeConfig.primary_color_light;
    const secondaryColor = isDark ? themeConfig.secondary_color_dark : themeConfig.secondary_color_light;
    const accentColor = isDark ? themeConfig.accent_color_dark : themeConfig.accent_color_light;
    const bgColor = isDark ? themeConfig.background_dark : themeConfig.background_light;
    
    // Primary colors with automatic contrast
    root.style.setProperty('--primary', primaryColor);
    root.style.setProperty('--primary-foreground', getContrastingForeground(primaryColor));
    root.style.setProperty('--ring', primaryColor);
    
    // Secondary color with automatic contrast
    root.style.setProperty('--secondary', secondaryColor);
    root.style.setProperty('--secondary-foreground', getContrastingForeground(secondaryColor));
    
    // Accent color with automatic contrast
    root.style.setProperty('--accent', accentColor);
    root.style.setProperty('--accent-foreground', getContrastingForeground(accentColor));
    
    // Background with automatic contrast
    root.style.setProperty('--background', bgColor);
    root.style.setProperty('--foreground', getContrastingForeground(bgColor));
    
    // Parse HSL values and create derived colors for card, popover, muted
    const bgParts = bgColor.split(' ');
    if (bgParts.length >= 3) {
      const h = parseFloat(bgParts[0]) || 0;
      const s = parseFloat(bgParts[1]) || 0;
      const l = parseFloat(bgParts[2]) || 0;
      
      let cardColor: string;
      let popoverColor: string;
      let mutedColor: string;
      
      if (isDark) {
        // Dark mode: slightly lighter surfaces
        cardColor = `${h} ${Math.max(0, s * 0.9)}% ${Math.min(100, l + 3)}%`;
        popoverColor = `${h} ${Math.max(0, s * 0.9)}% ${Math.min(100, l + 3)}%`;
        mutedColor = `${h} ${Math.max(0, s * 0.8)}% ${Math.min(100, l + 7)}%`;
      } else {
        // Light mode: same or slightly darker surfaces
        cardColor = bgColor;
        popoverColor = bgColor;
        mutedColor = `${h} ${Math.min(100, s * 0.7)}% ${Math.max(0, l - 4)}%`;
      }
      
      root.style.setProperty('--card', cardColor);
      root.style.setProperty('--card-foreground', getContrastingForeground(cardColor));
      root.style.setProperty('--popover', popoverColor);
      root.style.setProperty('--popover-foreground', getContrastingForeground(popoverColor));
      root.style.setProperty('--muted', mutedColor);
      root.style.setProperty('--muted-foreground', getMutedForeground(bgColor));
    }
  };

  const updateTheme = async (newTheme: Partial<ThemeConfig>) => {
    try {
      const { data: existingTheme } = await supabase
        .from('theme_config')
        .select('*')
        .single();

      if (existingTheme) {
        const { error } = await supabase
          .from('theme_config')
          .update(newTheme)
          .eq('id', existingTheme.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('theme_config')
          .insert([newTheme]);

        if (error) throw error;
      }

      const updatedTheme = { ...theme, ...newTheme };
      setTheme(updatedTheme);
      themeRef.current = updatedTheme;
      applyTheme(updatedTheme);
      
      // Update cache
      localStorage.setItem(THEME_CACHE_KEY, JSON.stringify({
        theme: updatedTheme,
        timestamp: Date.now()
      }));
      
      return true;
    } catch (error) {
      logError(error, 'useThemeConfig.updateTheme');
      return false;
    }
  };

  return { theme, isLoading, updateTheme, applyTheme };
};
